import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LEGAL_UI } from "@/lib/legal";

const KEY = "olime_cookie_consent";
export const openCookiePreferences = () =>
  window.dispatchEvent(new CustomEvent("open-cookie-preferences"));

export const CookieBanner = () => {
  const { lang } = useLang();
  const ui = (LEGAL_UI[lang] || LEGAL_UI.fr).banner;
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, functional: false });

  useEffect(() => {
    const stored = localStorage.getItem(KEY);
    if (!stored) setOpen(true);
    const reopen = () => { setCustomize(true); setOpen(true); };
    window.addEventListener("open-cookie-preferences", reopen);
    return () => window.removeEventListener("open-cookie-preferences", reopen);
  }, []);

  const persist = (value) => {
    localStorage.setItem(KEY, JSON.stringify({ ...value, necessary: true, ts: Date.now() }));
    setOpen(false);
    setCustomize(false);
  };

  if (!open) return null;

  return (
    <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-6">
      <div className="mx-auto max-w-3xl bg-olime-ink text-olime-bg shadow-2xl border border-white/10">
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm leading-relaxed text-white/80">{ui.text}</p>
            <button aria-label="close" data-testid="cookie-close" onClick={() => persist({ analytics: false, functional: false })} className="text-white/50 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {customize && (
            <div className="mt-6 space-y-4 border-t border-white/10 pt-6" data-testid="cookie-customize-panel">
              {[
                { key: "necessary", label: ui.necessary, desc: ui.necessaryDesc, locked: true },
                { key: "functional", label: ui.functional, desc: ui.functionalDesc },
                { key: "analytics", label: ui.analytics, desc: ui.analyticsDesc },
              ].map((row) => (
                <label key={row.key} className="flex items-start gap-4 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`cookie-toggle-${row.key}`}
                    disabled={row.locked}
                    checked={row.locked ? true : prefs[row.key]}
                    onChange={(e) => setPrefs((s) => ({ ...s, [row.key]: e.target.checked }))}
                    className="mt-1 accent-olime-sand w-4 h-4"
                  />
                  <span>
                    <span className="block text-sm text-white">{row.label}</span>
                    <span className="block text-xs text-white/50">{row.desc}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row flex-wrap items-center gap-3">
            {!customize && (
              <button data-testid="cookie-customize" onClick={() => setCustomize(true)} className="text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors order-3 sm:order-1">
                {ui.customize}
              </button>
            )}
            <div className="flex-1" />
            <button data-testid="cookie-refuse" onClick={() => persist({ analytics: false, functional: false })} className="order-2 w-full sm:w-auto px-6 py-3 text-xs uppercase tracking-[0.2em] border border-white/30 text-white hover:bg-white/10 transition-colors duration-300">
              {ui.refuse}
            </button>
            {customize ? (
              <button data-testid="cookie-save" onClick={() => persist(prefs)} className="btn-light order-1 sm:order-3 w-full sm:w-auto">
                {ui.save}
              </button>
            ) : (
              <button data-testid="cookie-accept" onClick={() => persist({ analytics: true, functional: true })} className="btn-light order-1 sm:order-3 w-full sm:w-auto">
                {ui.accept}
              </button>
            )}
          </div>

          <div className="mt-4">
            <Link to="/legal/cookies" onClick={() => setOpen(false)} className="text-xs text-white/50 underline hover:text-white transition-colors">
              {ui.policy}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
